import React, { useEffect } from "react";

import {
  faChevronDown,
  faChevronUp,
  faPlus,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { faSignIn } from "@fortawesome/pro-regular-svg-icons";
import {
  faCopy,
  faPencil,
  faSignOut,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import Grid from "@material-ui/core/Grid";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";

import {
  closeConfirmModal,
  MasterRuleAction,
  MasterRuleBookAction,
  openConfirmModal,
  RuleBookAction,
} from "actions";

import { MultiButton } from "components/Button";
import { Chip, ChipWrapper } from "components/Chip";
import { CollapseIcon } from "components/Icons/CollapseIcon";
import { Paper } from "components/Paper";
import { StatusWrapper } from "components/ScanningPenCards/Card/elements";

import { RuleValueFormat, ValueSource } from "constants/ruleBooks";

import { CustomiseManagedRuleBookLibraryForm } from "containers/Settings/RuleBooks/Library/CustomiseManagedRuleBookLibraryForm";
import { getGstFee } from "containers/Settings/RuleBooks/Rules/CustomRules/Value/ValueClause";

import { EMPTY_ARRAY } from "lib";

import {
  getIsStaff,
  getMasterRuleBookById,
  getMasterRuleIdsByMasterRuleBookId,
  getMasterRulesByMasterRuleBookId,
  getRuleBookById,
  getRuleBookIdsByMasterRuleBookId,
} from "selectors";

import { useToggle } from "hooks";

function getDefaultRuleContent() {
  return JSON.stringify({
    criteria: [],
    from_business: {
      source: "",
      value: "",
      criteria_output_index: "",
    },
    input_field: null,
    price_multiplier: 1,
    to_business: {
      source: "",
      value: "",
      criteria_output_index: "",
    },
    value: {
      amount: { source: ValueSource.CONSTANT, value: 0 },
      offset: { source: ValueSource.CONSTANT, value: 0 },
      rounding_value: 0,
      unit_offset: { source: ValueSource.CONSTANT, value: 0 },
      unit_limit: { source: ValueSource.CONSTANT, value: 0 },
      units: {
        source: "",
        value: "",
        criteria_output_index: "",
      },
      fees: [getGstFee("exclusive")],
    },
    include_if_zero: false,
  });
}

interface MasterRuleBookLibraryCardProps {
  masterRuleBookId: string;
  ruleBookId: string;
  masterRuleId: string;
  showSoftDeleted: boolean;
}

export function MasterRuleBookLibraryCard(
  props: MasterRuleBookLibraryCardProps,
) {
  const { masterRuleBookId, ruleBookId, masterRuleId, showSoftDeleted } = props;

  const masterRuleIds =
    useSelector(getMasterRuleIdsByMasterRuleBookId(masterRuleBookId)) ||
    EMPTY_ARRAY;

  const [isExpanded, toggleIsExpanded] = useToggle(false);
  useEffect(() => {
    const isRuleBookActive = masterRuleIds.includes(masterRuleId);
    if (!isExpanded && isRuleBookActive) {
      toggleIsExpanded();
    }
  }, [isExpanded, masterRuleId, masterRuleIds, toggleIsExpanded]);

  const dispatch = useDispatch();

  const masterRuleBook = useSelector(getMasterRuleBookById(masterRuleBookId));
  const ruleBook = useSelector(getRuleBookById(ruleBookId));
  const isStaff = useSelector(getIsStaff);

  const isSubscribed = useSelector(
    state =>
      getRuleBookIdsByMasterRuleBookId(masterRuleBookId)(state) || EMPTY_ARRAY,
  ).includes(ruleBookId);

  const isArchived = masterRuleBook.is_archived;

  const nextMasterRuleOrder = useSelector(
    state =>
      (getMasterRulesByMasterRuleBookId(masterRuleBookId)(state) || EMPTY_ARRAY)
        .length,
  );

  function onClickAddMasterRule() {
    dispatch(
      MasterRuleAction.create({
        billing_tags: [],
        category: "",
        comment: "",
        content: getDefaultRuleContent(),
        gl_code: { source: ValueSource.CONSTANT, value: "" },
        id: v4(),
        name: "New Master Rule",
        order: nextMasterRuleOrder,
        quantity_output_format: RuleValueFormat.UNITS,
        quantity_raw_format: RuleValueFormat.UNITS,
        rule_book_id: masterRuleBookId,
        unit_amount_output_format: RuleValueFormat.DOLLARS,
        unit_amount_raw_format: RuleValueFormat.CENTS,
      }),
    );
  }

  function onClickCopyMasterRuleBook() {
    dispatch(MasterRuleBookAction.copy(masterRuleBookId));
  }

  function onClickEditMasterRuleBook() {
    window.open(
      queryString.stringifyUrl({
        url: `/admin/agrinous_rules/masterrulebook/${masterRuleBookId}/change/?_to_field=cbid`,
        query: { q: masterRuleBook.name },
      }),
      "_blank",
    );
  }

  function onClickConfirmDeleteMasterRule() {
    dispatch(MasterRuleBookAction.delete(masterRuleBookId));

    dispatch(closeConfirmModal());
  }

  function onClickCancelConfirmDeleteMasterRuleBook() {
    dispatch(closeConfirmModal());
  }

  function onClickDeleteMasterRuleBook() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: "Are you sure you want to delete this Master Rule Book?",
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelConfirmDeleteMasterRuleBook,
            secondary: true,
          },
          {
            label: "Delete Master Rule Book",
            onClick: onClickConfirmDeleteMasterRule,
            secondary: false,
          },
        ],
      }),
    );
  }

  function onClickConfirmUnsubscribe() {
    dispatch(
      RuleBookAction.unsubscribeFromMasterRuleBook({
        id: ruleBookId,
        masterRuleBookId,
      }),
    );
    dispatch(closeConfirmModal());
  }

  function onClickCancelConfirm() {
    dispatch(closeConfirmModal());
  }

  function onClickUnsubscribe() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message:
          "Are you sure you want to unsubscribe from this and all other rules from Master Rule Book?",
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelConfirm,
            secondary: true,
          },
          {
            label: "Unsubscribe from Master Rule Book",
            onClick: onClickConfirmUnsubscribe,
            secondary: false,
          },
        ],
      }),
    );
  }

  function onClickConfirmSynchronizeAll() {
    dispatch(
      MasterRuleBookAction.synchronizeAll({
        id: masterRuleBookId,
      }),
    );
    dispatch(closeConfirmModal());
  }

  function onClickSynchronizeAll() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message:
          "Are you sure you want to synchronise this Master Rule Book with all Rule Books that reference it?",
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelConfirm,
            secondary: true,
          },
          {
            label: "Synchronise EVERYWHERE",
            onClick: onClickConfirmSynchronizeAll,
            secondary: false,
          },
        ],
      }),
    );
  }

  function onClickSynchronizeRulebook() {
    dispatch(
      RuleBookAction.synchronizeWithMasterRuleBook({
        id: ruleBookId,
        masterRuleBookId,
      }),
    );
  }

  const buttons = [
    {
      title: "Subscribe",
      isDisabled: isSubscribed || !masterRuleIds.length,
      tooltip:
        isSubscribed || !masterRuleIds.length
          ? "Master rulebook must have at least 1 master rule to be able to subscribe"
          : null,
      onClick: onClickSynchronizeRulebook,
      default: !isSubscribed,
      icon: faSignIn,
      dataTour: "subscribeToRuleBook",
    },
    {
      title: "Unsubscribe",
      isDisabled: !isSubscribed,
      onClick: onClickUnsubscribe,
      default: isSubscribed,
      icon: faSignOut,
      dataTour: "unsubscribeFromRuleBook",
    },
    isSubscribed && {
      title: `Synchronise with ${ruleBook.name}`,
      isDisabled: !isSubscribed,
      onClick: onClickSynchronizeRulebook,
      default: isSubscribed,
      icon: faSync,
      dataTour: "updateRuleBook",
    },
    {
      title: "Synchronise Everywhere",
      onClick: onClickSynchronizeAll,
      icon: faSync,
      dataTour: "syncrhonizeMasterRuleBook",
    },
  ].filter(Boolean);

  if (isStaff) {
    buttons.push({
      title: "Edit Master Rule Book",
      isDisabled: false,
      onClick: onClickEditMasterRuleBook,
      default: false,
      icon: faPencil,
      dataTour: "editMasterRuleBook",
    });
    buttons.push({
      title: "Add Master Rule",
      isDisabled: !isSubscribed && Boolean(masterRuleIds.length),
      onClick: onClickAddMasterRule,
      default: false,
      icon: faPlus,
      dataTour: "addMasterRule",
    });
    buttons.push({
      title: "Copy Master Rule Book",
      isDisabled: false,
      onClick: onClickCopyMasterRuleBook,
      default: false,
      icon: faCopy,
      dataTour: "copyMasterRuleBook",
    });
    buttons.push({
      title: "Delete Master Rule Book",
      isDisabled: false,
      onClick: onClickDeleteMasterRuleBook,
      default: false,
      icon: faTrash,
      dataTour: "deleteMasterRuleBook",
    });
  }

  if (isArchived && !isSubscribed) {
    return null;
  }

  return (
    <StatusWrapper color={isSubscribed ? "primary" : "grayDisabled"}>
      <Paper>
        <Grid container spacing={2}>
          <Grid item xs={2} sm={1}>
            <ChipWrapper>
              {isSubscribed ? (
                <Chip backgroundColor="primary" color="white">
                  Subscribed
                </Chip>
              ) : (
                <Chip backgroundColor="grayDisabled">Not Subscribed</Chip>
              )}
            </ChipWrapper>
          </Grid>
          <Grid item xs={8}>
            {masterRuleBook.name}
          </Grid>

          <Grid item xs={3} container justifyContent="flex-end">
            <MultiButton buttons={buttons} />
          </Grid>

          <Grid item xs={10}>
            <blockquote>{masterRuleBook.description}</blockquote>
          </Grid>
          <Grid item xs={2} container justifyContent="flex-end">
            {isSubscribed && (
              <CollapseIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
                onClick={toggleIsExpanded}
              />
            )}
          </Grid>

          {isSubscribed && isExpanded && (
            <Grid item xs={12}>
              <CustomiseManagedRuleBookLibraryForm
                masterRuleBookId={masterRuleBookId}
                onAfterSave={toggleIsExpanded}
                ruleBookId={ruleBookId}
                masterRuleId={masterRuleId}
                showSoftDeleted={showSoftDeleted}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </StatusWrapper>
  );
}
